import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Field, Input, Link as FluentLink, MessageBar, MessageBarBody, Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import { Checkmark16Filled, Location16Regular, Search16Regular } from "@fluentui/react-icons";
import { api } from "../lib/api.ts";

interface Result { ref: string; name: string; postcode: string | null; label: string; short?: string }
type Lookup =
  | { mode: "maps" | "list"; status: "confirm"; result: Result; question: string }
  | { mode: "maps" | "list"; status: "choose"; options: Result[]; question: string }
  | { mode: "maps" | "list"; status: "none"; question: string };

/** What the trainer ended up choosing; maps straight onto the API's gym fields. */
export type GymChoice =
  | { kind: "ref"; placeRef: string; label: string; via: "api" | "disambiguated" | "list" }
  | { kind: "manual"; name: string; postcode: string; label: string; via: "manual" };

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "8px" },
  row: { display: "flex", gap: "8px", flexWrap: "wrap" },
  box: { display: "flex", flexDirection: "column", gap: "8px", padding: "12px", borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground3 },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px" },
  chosen: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
});

/**
 * "Where do you train?" — type anything ("PureGym Boucher", "the leisure centre in Lisburn") or use
 * your location once. Confirms with branch + postcode; asks which branch if it's not obvious; falls
 * back to name + postcode for independents.
 */
export function GymLookup({ value, onChange }: { value: GymChoice | null; onChange: (v: GymChoice | null) => void }) {
  const s = useStyles();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<Lookup | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [manual, setManual] = useState<{ name: string; postcode: string } | null>(null);
  const health = useQuery({ queryKey: ["health"], queryFn: () => api.get<{ maps?: boolean }>("/health"), staleTime: 3_600_000 });
  const canLocate = !!health.data?.maps && "geolocation" in navigator;

  const run = async (body: { q?: string; lat?: number; lon?: number }) => {
    setBusy(true);
    setErr(null);
    setManual(null);
    try {
      const r = await api.post<Lookup>("/api/gyms/lookup", body);
      setRes(r);
      if (r.status === "none") setManual({ name: body.q ?? "", postcode: "" });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const useLocation = () => {
    if (!navigator.geolocation) return setErr("Your browser can't share location — type your gym instead.");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => void run({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => {
        setBusy(false);
        setErr("No problem — type your gym's name instead.");
      },
      { maximumAge: 600_000, timeout: 10_000 },
    );
  };

  const pick = (r: Result, via: "api" | "disambiguated" | "list") => {
    onChange({ kind: "ref", placeRef: r.ref, label: r.label, via: r.ref.startsWith("gym:") ? "list" : via });
    setRes(null);
  };

  if (value) {
    return (
      <div className={s.chosen}>
        <Checkmark16Filled color={tokens.colorPaletteGreenForeground1} />
        <Text weight="semibold">{value.label}</Text>
        <FluentLink as="button" onClick={() => onChange(null)}>Change</FluentLink>
      </div>
    );
  }

  return (
    <div className={s.root}>
      {/* Not a <form>: this sits inside the sign-up form, and forms can't nest. */}
      <div className={s.row} role="search">
        <Input style={{ flex: 1, minWidth: 200 }} value={q} onChange={(_, d) => setQ(d.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (q.trim().length >= 2) void run({ q: q.trim() }); } }} placeholder="e.g. PureGym Boucher Road, or the leisure centre in Lisburn" aria-label="Search for your gym" contentBefore={<Search16Regular />} />
        <Button type="button" disabled={q.trim().length < 2 || busy} onClick={() => void run({ q: q.trim() })}>Find</Button>
        {canLocate && <Button type="button" appearance="subtle" icon={<Location16Regular />} onClick={useLocation} disabled={busy}>Use my location</Button>}
      </div>
      {canLocate && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Your location is only used for this search — we don't store it.</Text>}
      {busy && <Spinner size="tiny" label="Looking…" labelPosition="after" />}
      {err && <MessageBar intent="warning"><MessageBarBody>{err}</MessageBarBody></MessageBar>}

      {res?.status === "confirm" && (
        <div className={s.box}>
          <Text weight="semibold">{res.question}</Text>
          <div className={s.chips}>
            <Button type="button" appearance="primary" size="small" onClick={() => pick(res.result, "api")}>Yes, that's it</Button>
            <Button type="button" size="small" onClick={() => { setRes(null); setManual({ name: q, postcode: "" }); }}>No — it's somewhere else</Button>
          </div>
        </div>
      )}
      {res?.status === "choose" && (
        <div className={s.box}>
          <Text weight="semibold">{res.question}</Text>
          <div className={s.chips}>
            {res.options.map((o) => (
              <Button type="button" key={o.ref} size="small" shape="circular" onClick={() => pick(o, "disambiguated")}>{o.label}</Button>
            ))}
            <Button type="button" size="small" shape="circular" appearance="subtle" onClick={() => { setRes(null); setManual({ name: q, postcode: "" }); }}>None of these</Button>
          </div>
        </div>
      )}
      {manual && (
        <div className={s.box}>
          <Text weight="semibold">{res?.status === "none" ? res.question : "Tell me the gym's name and postcode."}</Text>
          <div className={s.row}>
            <Field label="Gym name" size="small" style={{ flex: 2, minWidth: 160 }}>
              <Input size="small" value={manual.name} onChange={(_, d) => setManual({ ...manual, name: d.value })} />
            </Field>
            <Field label="Postcode" size="small" style={{ flex: 1, minWidth: 100 }}>
              <Input size="small" value={manual.postcode} onChange={(_, d) => setManual({ ...manual, postcode: d.value.toUpperCase() })} />
            </Field>
          </div>
          <Button
            type="button"
            size="small"
            appearance="primary"
            style={{ alignSelf: "flex-start" }}
            disabled={manual.name.trim().length < 2 || !/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(manual.postcode.trim())}
            onClick={() => onChange({ kind: "manual", name: manual.name.trim(), postcode: manual.postcode.trim(), label: `${manual.name.trim()}, ${manual.postcode.trim()}`, via: "manual" })}
          >
            Use this gym
          </Button>
        </div>
      )}
    </div>
  );
}

/** API body fields for a choice. */
export function gymFields(c: GymChoice) {
  return c.kind === "ref" ? { placeRef: c.placeRef, gymResolvedVia: c.via } : { newGym: { name: c.name, postcode: c.postcode }, gymResolvedVia: c.via };
}
